import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Typography, Grid, Box, Paper, Chip, Alert, CircularProgress,
  FormControl, Select, MenuItem, TextField, Button, Checkbox, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton, TableContainer
} from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/Delete';
import CommentIcon from '@mui/icons-material/Comment';
import {getUserFriendlyError} from "../utils/errorHandler";
import AccountPickerMulti from '../components/AccountPickerMulti';
import type { AccountGroup } from '../components/AccountPickerMulti';
import { useFieldValidation } from '../hooks/useFieldValidation';
import type { FieldLimit } from '../hooks/useFieldValidation';

// ==== Границы полей: 1-в-1 из StartNeuroCommentCommand (pydantic ge/le) ====
const NC_LIMITS: Record<string, FieldLimit> = {
  minMessageLength: { min: 10, max: 1000, integer: true, helper: 'По умолчанию: 20' },     // ge=10 le=1000
  hourlyLimit: { min: 1, max: 100, integer: true, helper: 'По умолчанию: 20' },            // ge=1 le=100
  dailyLimit: { min: 1, max: 1000, integer: true, helper: 'По умолчанию: 100' },           // ge=1 le=1000
  minCommentInterval: { min: 30, max: 3600, integer: true, helper: 'По умолчанию: 180 сек' }, // ge=30 le=3600
};


interface Account {
  id: string;
  name: string;
  phone_number: string;
}

interface ActiveCommenter {
  task_id: string;
  account_names: string[];
  accounts_count: number;
  channels: string[];
  prompt?: string | null;
  tone?: string | null;
  status: string;
  started_at?: string | null;
  uptime_seconds?: number | null;
}



const NeuroCommenting: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [activeCommenters, setActiveCommenters] = useState<ActiveCommenter[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [tab, setTab] = useState<'setup' | 'active'>('setup');

  // Форма — все поля из нового API
  const [accountNames, setAccountNames] = useState<string[]>([]);
  const [channels, setChannels] = useState('');
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('');
  const [minMessageLength, setMinMessageLength] = useState(20);
  const [hourlyLimit, setHourlyLimit] = useState(20);
  const [dailyLimit, setDailyLimit] = useState(100);
  const [minCommentInterval, setMinCommentInterval] = useState(180);

  const validation = useFieldValidation({ limits: NC_LIMITS });
  const [useDefaultMessages, setUseDefaultMessages] = useState(false);
  const [messageWithoutGpt, setMessageWithoutGpt] = useState('');

  useEffect(() => {
    if (!auth?.isAuthenticated) return;
    fetchData();
    const interval = setInterval(fetchActive, 7000);
    return () => clearInterval(interval);
  }, [auth?.isAuthenticated]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [accRes, activeRes] = await Promise.all([
        api.get('/accounts'),
        api.get('/neuro_commenting/list'),
        api.get('/account_groups').then(r => setGroups(r.data || [])).catch(() => {})
      ]);
      setAccounts(accRes.data.accounts || []);
      setActiveCommenters(activeRes.data || []);
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка загрузки', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActive = async () => {
    try {
      const res = await api.get('/neuro_commenting/list');
      setActiveCommenters(res.data || []);
    } catch (err) {
      console.error('Ошибка обновления активных задач:', err);
    }
  };

  const resetForm = () => {
    setAccountNames([]);
    setChannels('');
    setPrompt('');
    setTone('');
    setMinMessageLength(20);
    setHourlyLimit(20);
    setDailyLimit(100);
    setMinCommentInterval(180);
    setUseDefaultMessages(false);
    setMessageWithoutGpt('');
  };

  const startNeuroCommenting = async () => {
    if (!accountNames.length || !channels.trim()) {
      setMessage({ text: 'Выберите хотя бы один аккаунт и канал', type: 'error' });
      return;
    }

    const snapshot = {
      minMessageLength: String(minMessageLength),
      hourlyLimit: String(hourlyLimit),
      dailyLimit: String(dailyLimit),
      minCommentInterval: String(minCommentInterval),
    };
    if (!validation.validateAll(snapshot)) {
      setMessage({ text: 'Проверьте выделенные поля — есть ошибки валидации', type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/neuro_commenting/start', {
        account_names: accountNames,
        channels: channels.split(',').map(c => c.trim()).filter(Boolean),
        prompt: prompt || null,
        tone: tone || null,
        min_message_length: minMessageLength,
        hourly_limit: hourlyLimit,
        daily_limit: dailyLimit,
        min_comment_interval: minCommentInterval,
        message_without_gpt: messageWithoutGpt || null,
        use_default_messages: useDefaultMessages,
      });

      setMessage({ text: 'Нейрокомментатор успешно запущен!', type: 'success' });
      setTab('active');
      resetForm();
      fetchActive();
    } catch (err: any){
  const text = getUserFriendlyError(err, accounts, 'нейрокомментатор');
  setMessage({ text, type: 'error' });
} finally {
      setIsLoading(false);
    }
  };

  const stopCommenter = async (taskId: string) => {
    setIsLoading(true);
    try {
      await api.delete(`/neuro_commenting/stop/${taskId}`);
      setMessage({ text: 'Остановлено', type: 'info' });
      fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка остановки', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const restoreCommenter = async (taskId: string) => {
    setIsLoading(true);
    try {
      await api.get(`/neuro_commenting/restart/${taskId}`);
      setMessage({ text: 'Возобновлён!', type: 'success' });
      fetchActive();
    } catch (err: any) {
    const text = getUserFriendlyError(err, accounts, 'возобновление нейрокомментатора');
    setMessage({ text, type: 'error' });
  } finally {
      setIsLoading(false);
    }
  };

  const deleteCommenter = async (taskId: string) => {
    if (!window.confirm('Удалить нейрокомментатор НАВСЕГДА? Это нельзя отменить.')) return;

    setIsLoading(true);
    try {
      await api.get(`/neuro_commenting/delete/${taskId}`);
      setMessage({ text: 'Удалено навсегда', type: 'info' });
      fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка удаления', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const getTabStyle = (current: 'setup' | 'active') => ({
    flex: 1,
    py: 2,
    borderRadius: 0,
    bgcolor: tab === current ? '#E0F2FE' : 'transparent',
    color: tab === current ? '#1E40AF' : '#6B7280',
    fontWeight: tab === current ? 600 : 400,
    '&:hover': { bgcolor: tab === current ? '#DBEAFE' : '#F3F4F6' }
  });

  if (isLoading && accounts.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography mt={2}>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      {/* Заголовок */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#F8FAFC', borderRadius: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 2 }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <CommentIcon sx={{ fontSize: 40, color: '#1E40AF' }} />
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A8A', mb: 1 }}>
              Нейрокомментинг
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label="AI-комментарии" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Генерация трафика" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Настройка лимитов" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              <Chip label="Статистика" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
            </Box>
            <Typography sx={{ color: '#4B5563', fontSize: '0.95rem', lineHeight: 1.6 }}>
              Автоматическое комментирование постов в каналах с AI или шаблонами.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Описание */}
      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          Как работает нейрокомментинг?
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Аккаунты мониторят новые посты в указанных каналах и оставляют естественные комментарии. Настройте профили аккаунтов или создайте прокси-каналы - и начните продвигать свои продукты в несколько кликов!
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Мультиаккаунт</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>Десятки комментаторов распределяются между каналами, подменяя друг друга при бане</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>AI + шаблоны</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>Если нейросеть недоступна — в ход пойдут шаблоны</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Безопасные лимиты</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>Часовые, суточные, интервалы между комментариями</Typography>
          </Box>

        </Box>
         <Typography sx={{ color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic' }}>
         Подходит для перенаправления органического трафика на ваши продукты и каналы
        </Typography>
        <Typography sx={{color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic'}}>
          Ознакомьтесь с документацией по автоматизации{' '}
          <Typography
              component="a"
              href="https://typhon.gitbook.io/docs/automatizations_telegram/neurocommenting"
              sx={{textDecoration: 'underline', cursor: 'pointer'}}
          >
            здесь
          </Typography>
        </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex' }}>
          <Button onClick={() => { setTab('setup'); resetForm(); }} sx={getTabStyle('setup')} startIcon={<PlayArrowIcon />}>
            Настройка
          </Button>
          <Button onClick={() => setTab('active')} sx={getTabStyle('active')} startIcon={<StopIcon />}>
            Мои нейрокомментаторы
          </Button>
        </Box>

        <Box sx={{ p: 4 }}>
          {/* === ВКЛАДКА НАСТРОЙКИ === */}
          {tab === 'setup' && (
            <Grid container spacing={3}>
              {/* Аккаунты */}
              <Grid size={12}>
                <AccountPickerMulti
                  accounts={accounts}
                  selected={accountNames}
                  onChange={setAccountNames}
                  groups={groups}
                />
              </Grid>

              {/* Каналы */}
              <Grid size={12}>
                <TextField
                  label="Каналы"
                  value={channels}
                  onChange={(e) => setChannels(e.target.value)}
                  fullWidth
                  required
                  placeholder="channel1, @mychannel, https://t.me/..."
                  helperText="Через запятую или по одному"
                />
              </Grid>

              {/* AI-настройки */}
              <Grid size={12}>
                <TextField
                  label="Промпт для AI (опционально)"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Напиши живой, естественный комментарий от лица обычного подписчика..."
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Тон комментариев (опционально)"
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  fullWidth
                  placeholder="дружелюбный, экспертный, с юмором"
                />
              </Grid>

              {/* Лимиты */}
              <Grid size={6}>
                <TextField
                  label="Мин. длина поста"
                  {...validation.numberFieldProps('minMessageLength', minMessageLength, setMinMessageLength, 20)}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Комментариев в час"
                  {...validation.numberFieldProps('hourlyLimit', hourlyLimit, setHourlyLimit, 20)}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Комментариев в день"
                  {...validation.numberFieldProps('dailyLimit', dailyLimit, setDailyLimit, 100)}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Интервал между комментариями (сек)"
                  {...validation.numberFieldProps('minCommentInterval', minCommentInterval, setMinCommentInterval, 180)}
                  fullWidth
                />
              </Grid>

              {/* Фолбэк */}
              <Grid size={12}>
                <FormControlLabel
                  control={<Checkbox checked={useDefaultMessages} onChange={(e) => setUseDefaultMessages(e.target.checked)} />}
                  label="Только шаблонные фразы (без AI)"
                />
              </Grid>

              <Grid size={12}>
                  <TextField
                      label={useDefaultMessages ? "Комментарий (шаблон)" : "Резервный комментарий"}
                      value={messageWithoutGpt}
                      onChange={(e) => setMessageWithoutGpt(e.target.value)}
                      fullWidth
                      placeholder="Круто!, Спасибо за пост!, Интересно!"
                      helperText={
                          useDefaultMessages
                              ? "Будет использоваться как основной текст"
                              : "Используется, если AI недоступен"
                      }
                  />
              </Grid>

              {/* Запуск */}
              <Grid size={12}>
                <Button
                  variant="contained"
                  onClick={startNeuroCommenting}
                  disabled={isLoading || !accountNames.length || !channels.trim() || validation.hasErrors}
                  startIcon={isLoading ? <CircularProgress size={22} /> : <PlayArrowIcon />}
                  fullWidth
                  sx={{ py: 2, fontSize: '1.1rem', bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
                >
                  {isLoading ? 'Запуск...' : 'Запустить'}
                </Button>
              </Grid>
            </Grid>
          )}

          {/* === ВКЛАДКА АКТИВНЫЕ === */}
          {tab === 'active' && (
            <ActiveTab
              commenters={activeCommenters}
              stop={stopCommenter}
              restore={restoreCommenter}
              remove={deleteCommenter}
              isLoading={isLoading}
              setMessage={setMessage}
              fetchActive={fetchActive}
              accounts={accounts}
            />
          )}
        </Box>
      </Paper>
    </Container>
  );
};

// === Активные задачи ===
const ActiveTab: React.FC<{
  commenters: ActiveCommenter[];
  stop: (id: string) => Promise<void>;
  restore: (id: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
  isLoading: boolean;
  setMessage: (msg: { text: string; type: 'success' | 'error' | 'info' } | null) => void;
  fetchActive: () => Promise<void>;
  accounts: Account[];
}> = ({ commenters, stop, restore, remove, isLoading, setMessage, fetchActive, accounts }) => {
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);

  const handleDelete = async (taskId: string) => {
    if (!window.confirm('Удалить НАВСЕГДА?')) return;
    setDeleting(taskId);
    await remove(taskId);
    setDeleting(null);
  };

  const handleRestore = async (taskId: string) => {
    setRestoring(taskId);
    await restore(taskId);
    setRestoring(null);
  };

  if (commenters.length === 0) {
    return <Typography textAlign="center" py={8} color="text.secondary" variant="h6">Нет активных нейрокомментаторов</Typography>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>ID</strong></TableCell>
            <TableCell><strong>Аккаунты</strong></TableCell>
            <TableCell><strong>Каналы</strong></TableCell>
            <TableCell><strong>Статус</strong></TableCell>
            <TableCell><strong>Время</strong></TableCell>
            <TableCell align="right"><strong>Действия</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {commenters.map((c) => (
            <TableRow
              key={c.task_id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate(`/neuro_commenting/detail/${c.task_id}`)}
            >
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                {c.task_id.slice(0, 8)}
              </TableCell>
              <TableCell>{c.accounts_count} шт.</TableCell>
              <TableCell sx={{ fontSize: '0.85rem' }}>{c.channels.length} кан.</TableCell>
              <TableCell>
                <Chip
                  label={c.status === 'running' ? 'Работает' : c.status === 'stopped' ? 'Остановлен' : c.status}
                  size="small"
                  color={c.status === 'running' ? 'success' : c.status === 'stopped' ? 'warning' : 'default'}
                />
              </TableCell>
              <TableCell>{c.uptime_seconds ? `${Math.floor(c.uptime_seconds / 60)}м` : '—'}</TableCell>

              <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                  {c.status === 'stopped' && (
                    <IconButton size="small" color="success" onClick={() => handleRestore(c.task_id)} disabled={restoring === c.task_id}>
                      {restoring === c.task_id ? <CircularProgress size={16} /> : <PlayArrowIcon fontSize="small" />}
                    </IconButton>
                  )}
                  {c.status === 'running' && (
                    <IconButton size="small" color="error" onClick={() => stop(c.task_id)} disabled={isLoading}>
                      <StopIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(c.task_id)}
                    disabled={deleting === c.task_id}
                  >
                    {deleting === c.task_id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default NeuroCommenting;